/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  FormInput,
  MITheme
} from "@spectrumselect/mi-ui-react";
import PassInput from "../../components/shared/PasswordInput/PasswordInput"
import { ErrorMessage } from "../../components/errorMessage/ErrorMessage";
import { SuccessMessage } from "../../components/successMessage/SuccessMessage";
import { updateStandardFormErrorMessage } from "../../redux/AppStateSlice";
import { useDispatch } from "react-redux";
import { local } from "../../internationalization/Localization";
import "./UsernameForm.scss";
import i1 from "../../images/login-i1.png"
import i2 from "../../images/login-i2.png"
import i3 from "../../images/login-i3.png"
import i7 from "../../images/login-i7.png"
import i8 from "../../images/login-i8.png"
import i9 from "../../images/login-i9.png"
import i10 from "../../images/login-i10.png"
import { TENANT, MAILTO } from "../../utils/constants";
import { getSafeHost } from "../../utils/urlUtils";

export interface UsernameProps {
  errorMessage?: string;
  onEnter(username: string, password: string): void;
  invokeForgotPasswordJourney(): void;
  changePasswordInit?(): void;
  invokeLoginJourney?(username: string, password: string): void;
  tenant?: string;
  host?: string;
  successMessage?: string;
  sso_next?: string;
}

const UsernameForm: React.FunctionComponent<UsernameProps> = ({ onEnter, errorMessage, invokeForgotPasswordJourney, tenant, host, successMessage, sso_next }) => {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [success, setSuccess] = useState(successMessage);
  // const [wrongPassCount, setWrongPassCount ] = useState(0)
  const ref: any = useRef(null)  //--- refs ---//
  useLayoutEffect(() => {
    ref?.current?.style?.setProperty("padding-left", "4px", "important");
  }, []);
  useEffect(() => {
    setSuccess(successMessage);
  }, [successMessage])

  const firstInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  useEffect(() => {
    if (errorMessage) {
      setSuccess("");
      setUserName("");
      setPassword("");
      firstInputRef.current?.focus();
    }
  }, [errorMessage])

  //--- callback methods ---//
  const onSubmitButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setSuccess("");
    dispatch(updateStandardFormErrorMessage("")); //clear errorMessage
    onEnter(userName, password);
  };

  const onResetPasswordButtonClick = () => {
    invokeForgotPasswordJourney();
  };

  const headingCSS = {
    fontSize: "18px",
    marginBottom: "10px",
    fontWeight: "600",
    color: "#333333"
  };
  const headingCSSRaymondJames = {
    fontSize: "20px",
    marginBottom: "10px",
    fontWeight: "400",
    color: "#262626"
  };
  const blackColor = {
    color: "#000",
  };

  const spanStyle = {
    display: "inline-block",
    padding: "15px 0 0",
    lineHeight: "1.5"
  };

  const handleTenantDescription = () => {
    if (tenant === TENANT.BLACKROCK) {
      return (
        <>
          <p className="txt2 welcome-text" style={headingCSS}>Welcome to Tax Managed BlackRock models, powered by 55ip.</p>
          <p className="txt2 welcome-desc">Select a BlackRock model and turn it into an automated tax managed strategy with 55ip. <a href="https://www.55-ip.com/BlackRock" rel="noreferrer" target="_blank">Learn more</a></p>
        </>
      )
    }
    if (tenant === TENANT.FOCUS) {
      return (
        <>
          <p className="txt2 welcome-text" style={headingCSS}>Welcome to 55ip’s tax-smart investment strategy engine.</p>
          <p className="txt2 welcome-desc">55ip’s intuitive experience and intelligent automation elevate portfolio design and delivery, helping advisors save time and seek better outcomes for their clients.<br /><br /><span className='onboard-text'>To register, please contact our Onboarding Team (<a href={MAILTO.link}>{MAILTO.text}</a>)</span>.</p>
        </>
      )
    }
    if (tenant === TENANT.JPMORGAN) {
      return (
        <>
          <p className="txt2 welcome-text" style={headingCSS}>Welcome to J.P. Morgan investment solutions, powered by tax-smart technology from 55ip.</p>
          <p className="txt2 welcome-desc">Learn how combining J.P. Morgan <a href="https://am.jpmorgan.com/us/en/asset-management/adv/investment-strategies/model-portfolios/tax-managed-solutions/" rel="noreferrer" target="_blank">models</a> or <a href="https://am.jpmorgan.com/us/en/asset-management/adv/products/j-p-morgan-custom-invest-u-s-large-cap-strategy-d6965d1a383a2441e0534e8862a9c741" rel="noreferrer" target="_blank">separately managed accounts</a> with 55ip can help you do more for your clients. Register below to get started.</p>
        </>
      )
    }
    if (tenant === TENANT.FIDELITY) {
      return (
        <>
          <p className="txt2 welcome-text" style={headingCSS}>Welcome to Fidelity models, combined with automated tax management from 55ip</p>
          <p className="txt2 welcome-desc">To learn more about utilizing models from Fidelity with automated tax strategies from 55ip, <a href="https://www.55-ip.com/fidelity" rel="noreferrer" target="_blank">click here</a>.</p>
        </>
      )
    }
    if (tenant === TENANT.RIVERFRONT) {
      return (
        <>
          <p className="txt2 welcome-text" style={headingCSS}>A smarter portfolio transition at Baird offered with 55ip tax management intelligent automation to Riverfront ETF Advantage and/or ALIGN 55ip Tax Managed Solutions.</p>
        </>
      )
    }
    if (tenant === TENANT.WISDOMTREE) {
      return (
        <>
          <p className="txt2 welcome-text" style={headingCSS}>Welcome to WisdomTree models, powered by automated tax management from 55ip.</p>
          <p className="txt2 welcome-desc">To learn more about combining models from WisdomTree with automated tax strategies from 55ip, <a href="https://www.55-ip.com/wisdomtree/" rel="noreferrer" target="_blank">click here</a>.</p>
        </>
      )
    }
    if (tenant === TENANT.RAYMONDJAMES) {
      return (
        <>
          <p className="raymondjames-txt2 welcome-text" style={headingCSSRaymondJames}>Welcome to J.P. Morgan Tax-Smart Direct Index Strategies, powered by 55ip.</p>
        </>
      )
    }
  };

  const JPMorganHTML = (
    <div className="sign_in_jpm_asset_mgmt">
      <div style={{ borderTop: "1px solid", marginTop: "40px", marginBottom: "40px", borderColor: "#E2E4E5", width: "358.01px" }}></div>
      <div style={{ border: "none" }}>
        <span style={{ fontStyle: "normal", fontWeight: "400", fontSize: "16px", lineHeight: "32px", fontFamily: "Roboto" }}>Already have a J.P. Morgan Asset Management account?</span>
      </div>
      <a style={{ display: "inline" }} href={sso_next ? atob(sso_next) : `https://am.jpmorgan.com/us/en/asset-management/adv/auth/login-form/?sso=55ip`}>
        <div style={{ border: "4px", marginTop: "20px", fontFamily: "Roboto" }}>
          <button type='button' className="asset_sign_in_btn jpmorgan-btn">Sign in with J.P Morgan Asset Management</button>
        </div>
      </a>
    </div>
  );

  const RiverFontHTML = (
    <div className="benefit-list">
      <div className="login100-form-custodian p-t-40 p-b-20" style={{ color: "#1C1C1C" }}>Diversified Solutions with Tax Managed Benefits</div>
        <div className="row clientlogos">
          <div className="container lbottomC">
            <div className="row">
              <div className="col-sm-6">
                <img src={i9} alt="Benefit 1" height="32px" />
                <p style={blackColor}><span style={spanStyle}>Explore RiverFront ETF Advantage<br />Model Portfolios and ALIGN 55ip Tax Managed Solutions.</span></p>
              </div> 
              <div className="col-sm-6"> <img src={i10} alt="Benefit 2" height="32px" /> 
                <p style={blackColor}><span style={spanStyle}>Explore Tax Management <br />Technology by 55ip.</span></p>
              </div>
          </div>
        </div>
      </div>
    </div>
  );
  
  const WisdomTreeHTML = (
    <div className="benefit-list">
      <div className="login100-form-custodian p-t-40 p-b-20" style={{ color: "#1C1C1C" }}>Explore more models and benefits below</div>
      <div className="row clientlogos">
        <div className="container lbottomC">
          <div className="row">
            <div className="col-sm-6">
              <img src={i7} alt="Benefit 1" height="32px" />
              <p style={blackColor}>
                <span style={spanStyle}>
                  Model Portfolios by<br />WisdomTree. Designed<br />to help provide diversification.
                </span>
              </p>
            </div>
            <div className="col-sm-6">
              <img src={i8} alt="Benefit 2" height="32px" />
              <p style={blackColor}>
                <span style={spanStyle}>
                  Tax-management<br />technology by 55ip.<br />Manage after-tax returns<br />with ease.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const blackRockHTML = (
    <div className="benefit-list">
      <div className="login100-form-custodian p-t-80 p-b-20">Login or register to access these benefits:</div>
        <div className="row clientlogos">
          <div className="container lbottomC">
            <div className="row">
              <div className="col-sm-4">
                <img src={i1} alt="Benefit 1" />
                <p>Access BlackRock Model Portfolio suite with the ability to implement with sub-advised or tradelist delivery agreement.</p>
              </div>
              <div className="col-sm-4">
                <img src={i2} alt="Benefit 2" />
                <p>Tax-aware transition and ongoing tax management.</p>
              </div>
              <div className="col-sm-4">
                <img src={i3} alt="Benefit 3" />
                <p>Easy to understand proposals and reports to illustrate potential tax benefits.</p>
              </div>
            </div>
          </div>
        </div>
    </div>
  );

  return (
    <form id="loginForm" name="loginForm" autoComplete="off" className="UsernameForm login-right-section">
      {handleTenantDescription()}
      <h3 className={tenant === TENANT.RAYMONDJAMES ? "raymondjames-signin" : "signIn"}>Sign in {tenant === TENANT.BLACKROCK && (
      <a className={`UsernameForm_ResetPwdButton blackrock-register`} href={`https://${getSafeHost(host || '')}/accounts/register/`}>
        Register
      </a>
      )}</h3>
      {!!errorMessage && <ErrorMessage onResetPassword={onResetPasswordButtonClick} className="UsernameForm_ErrorMessage">{errorMessage}</ErrorMessage>}
      {!!success && <SuccessMessage className="UsernameForm_ErrorMessage successMsg">{success}</SuccessMessage>}
      <div className={"App_formInputGroup UsernameForm_InputGroup"}>
        <FormInput
          ref={firstInputRef}
          id={"userID"}
          placeholder="Username"
          type={"text"}
          label=""
          value={userName}
          autoComplete={"userName"}
          // placeholder={local.get('userNameForm.formLabels.userId')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserName(e.target.value)}
        // theme={MITheme.dark}
        />
        <PassInput
          id={"password"}
          placeholder={local.get("userNameForm.formLabels.password")}
          label=""
          value={password}
          autoComplete={"password"}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
        // theme={MITheme.dark}
        />
        {/* <FormPasswordInput
          id={'password'}
          placeholder={local.get('userNameForm.formLabels.password')}
          label=""
          value={password}
          autoComplete={'password'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
        // theme={MITheme.dark}
        /> */}
      </div>
      {tenant === TENANT.FOCUS || tenant === TENANT.BLACKROCK ? (
      <div>
        <Button
            className={`login100-form-btn ${tenant}-btn`}
            type={"submit"}
            variant={ButtonVariant.primary}
            onClick={onSubmitButtonClick}
            disabled={userName.length === 0 || password.length === 0}
            theme={MITheme.dark}
            size={ButtonSize.large}
          >
            {local.get("userNameForm.buttonLabels[0]")}
        </Button>
        <a className={`UsernameForm_ResetPwdButton forget-pwd ${tenant}-text`} href={`https://${getSafeHost(host || '')}/accounts/forgot_username/`} >
          {local.get("userNameForm.buttonLabels[2]")}
        </a>
        <a className={`UsernameForm_ResetPwdButton ${tenant}-text`} href="javascript:void(0)" onClick={onResetPasswordButtonClick}>
          {local.get("userNameForm.buttonLabels[1]")}
        </a>
        <br />
        <br />
        {/* <Button
            className={`login100-form-btn forget-pwd ${tenant}-btn`}
            onClick={() => workforceLogin()}
            theme={MITheme.dark}
            size={ButtonSize.large}
          >
            {local.get('userNameForm.workforceLogin')}
          </Button> */}
      </div>
      )
        : (
          <>
            <div style={{ marginTop: tenant === TENANT.RAYMONDJAMES ? "-15px" : "0", marginBottom: tenant === TENANT.RAYMONDJAMES ? "20px" : "0" }}>
              <a
                className={`UsernameForm_ResetPwdButton forget-pwd ${tenant === TENANT.JPMORGAN || tenant === TENANT.RIVERFRONT || tenant === TENANT.FIDELITY || tenant === TENANT.WISDOMTREE ? `${tenant}-text` : ""}`}
                ref={ref}
                href={`https://${getSafeHost(host || '')}/accounts/forgot_username/`}>
                {local.get("userNameForm.buttonLabels[2]")}
              </a>
              <a
                className={`UsernameForm_ResetPwdButton ${tenant === TENANT.JPMORGAN || tenant === TENANT.RIVERFRONT || tenant === TENANT.FIDELITY || tenant === TENANT.WISDOMTREE ? `${tenant}-text` : ""}`}
                href="javascript:void(0)"
                onClick={onResetPasswordButtonClick}
                >
                {local.get("userNameForm.buttonLabels[1]")}
              </a>
            </div>
            <div>
            <Button
              className={`login100-form-btn forget-pwd ${tenant}-btn`}
              type={"submit"}
              onClick={onSubmitButtonClick}
              disabled={userName.length === 0 || password.length === 0}
              theme={MITheme.dark}
              size={ButtonSize.large}
            >
            {tenant === TENANT.JPMORGAN ? "Sign in with 55ip" : local.get("userNameForm.buttonLabels[0]")}
            </Button>
            { tenant === TENANT.RAYMONDJAMES ? "" :
            <>
              <span className="need-account">Need an account?</span> 
              <a style={{ paddingLeft: "3px" }}
                className={`UsernameForm_ResetPwdButton ${tenant === TENANT.JPMORGAN || tenant === TENANT.FIDELITY || tenant === TENANT.WISDOMTREE ? `${tenant}-text` : ""}`}
                href={`https://${getSafeHost(host || '')}/accounts/register/`}
              > Register </a> {tenant === TENANT.WISDOMTREE && "*"}</>}
            </div>
          </>
        )}
        
        {tenant === TENANT.JPMORGAN && (
          JPMorganHTML
        )}
        {tenant === TENANT.RIVERFRONT && (
          RiverFontHTML
        )}
        {tenant === TENANT.WISDOMTREE && (
          WisdomTreeHTML
        )}
        {tenant === TENANT.BLACKROCK && (
          blackRockHTML
        )}
    </form>
  );
};

export default UsernameForm;
